import React, { useCallback, useEffect, useRef, useState } from "react";
import { Btn, Eyebrow } from "../../components/Primitives";
import { api } from "../../lib/api";
import logger from "../../lib/logger";

export default function MessageThread({ user, matchId, onBack, onPlan }) {
    const [items, setItems] = useState([]);
    const [count, setCount] = useState(0);
    const [cap, setCap] = useState(10);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const scrollRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const r = await api.dating.listMessages(matchId, user.session_id);
            setItems(r.items || []);
            setCount(r.count || 0);
            setCap(r.cap || 10);
        } catch (e) { logger.error("messages load failed", e); }
    }, [matchId, user.session_id]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [items.length]);

    const send = async () => {
        const body = draft.trim();
        if (!body || sending || count >= cap) return;
        setSending(true);
        try {
            await api.dating.sendMessage(matchId, user.session_id, body);
            setDraft("");
            await load();
        } catch (e) {
            logger.error("send failed", e);
            alert(e?.response?.data?.detail || "Could not send.");
        }
        setSending(false);
    };

    const atCap = count >= cap;

    return (
        <div className="flex-1 flex flex-col bg-cream h-screen max-h-screen overflow-hidden" data-testid="messages-screen">
            <div className="px-6 pt-6 pb-2 flex items-center justify-between">
                <button data-testid="messages-back-btn" onClick={onBack} className="tap-target text-[11px] tracking-[0.28em] uppercase text-ink-muted">← Back</button>
                <span className="text-[10px] tracking-[0.32em] uppercase text-ink-muted" data-testid="messages-count">{count} / {cap}</span>
            </div>

            <div className="px-7 pt-4 pb-2">
                <Eyebrow>Light messaging</Eyebrow>
                <h1 className="mt-2 font-display text-2xl text-burgundy leading-[1.15]">
                    A few notes before <span className="italic">the date.</span>
                </h1>
                <p className="mt-1 text-xs text-ink-muted">
                    Capped at {cap} messages — enough to coordinate, not enough to overthink.
                </p>
            </div>

            <div ref={scrollRef} className="flex-1 min-h-0 px-6 py-4 overflow-y-auto space-y-2">
                {items.length === 0 && (
                    <div className="text-ink-muted text-sm py-10 text-center">
                        No messages yet. Say hello.
                    </div>
                )}
                {items.map(m => {
                    const mine = m.sender_user_id === user.session_id;
                    return (
                        <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                            <div
                                data-testid={`msg-${m.id}`}
                                className={`max-w-[78%] px-3 py-2 text-sm leading-snug ${mine ? "bg-burgundy text-cream" : "bg-white border border-burgundy/15 text-ink"}`}
                            >
                                {m.body}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="px-6 pb-safe-sm pt-3 border-t border-burgundy/10 bg-cream space-y-2">
                {atCap ? (
                    <div className="text-center py-2">
                        <p className="text-sm text-ink-muted mb-2">You've hit the message cap.</p>
                        {onPlan && (
                            <Btn data-testid="messages-plan-btn" variant="primary" onClick={onPlan}>Time to plan the date →</Btn>
                        )}
                    </div>
                ) : (
                    <div className="flex gap-2">
                        <input
                            data-testid="message-input"
                            type="text"
                            value={draft}
                            onChange={e => setDraft(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") send(); }}
                            maxLength={600}
                            placeholder="Type a message…"
                            className="flex-1 min-w-0 bg-white border border-burgundy/15 px-3 py-2.5 text-base text-ink focus:outline-none focus:border-burgundy/60"
                        />
                        <button
                            data-testid="message-send-btn"
                            onClick={send}
                            disabled={!draft.trim() || sending}
                            className="px-4 min-h-[44px] bg-burgundy text-cream text-xs tracking-[0.18em] uppercase disabled:opacity-50"
                        >Send</button>
                    </div>
                )}
            </div>
        </div>
    );
}
